import AccordionV2 from "@components/Accordion/AccordionV2";
import EventsAwardsAccordionHeader from "@events-awards/EventsAwardsAccordionHeader";

/**
 * Component to render Annual Report section for eligibility tab
 */
const AnnualReportSection = (): React.ReactElement => {
  return (
    <div className="bg-gray-150" data-testid="annual-report-section-component">
      <AccordionV2
        testId="annual-report"
        className="border-l border-r py-4"
        headerChildren={
          <EventsAwardsAccordionHeader title="Best Annual Report" />
        }
        arrowClassName="h-auto w-auto -rotate-90 mr-6 border-none"
        isArrowRight
      >
        <div className="mt-4 border-t border-gray-175 px-6 py-4">
          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Preliminary Screening Criteria
          </p>

          <p>
            The annual reports are first screened for the presence of the
            following information:
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>Discussion on corporate liquidity or capital management;</li>

            <li>
              Benchmarking commentaries against industry performance/statistics;
            </li>

            <li>Analysis and commentary on performance;</li>

            <li>Separate CEO/management&apos;s statement;</li>

            <li>Earnings prospects by division/territory;</li>

            <li>Discussion of key business risks;</li>

            <li>Discussion of risk management strategies;</li>

            <li>
              Presence of quantitative analysis of identified risks, including
              value-at-risk (VAR) analysis;
            </li>

            <li>
              Discussion on information technology risks, cyber security and
              personal data protection;
            </li>

            <li>
              Remuneration of directors disclosed in nearest dollar amount and
              not in bands;
            </li>

            <li>
              Remuneration of key management personnel disclosed in dollar
              amount and not in bands;
            </li>

            <li>
              Board succession planning/long tenure of independent directors;
            </li>

            <li>Policy/position on board diversity;</li>

            <li>
              Sustainability report disclosures included in annual report
              <ul className="list-outside list-disc pl-8">
                <li>Environment</li>

                <li>Community and social programmes</li>
              </ul>
            </li>

            <li>Quantification of sustainability performance;</li>

            <li>
              External assurance (including limited or reasonable assurance);
            </li>

            <li>Investor/shareholder relations activities;</li>

            <li>
              Employee diversity disclosure (gender, age, education,
              nationality, people with special needs, etc.);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Disclosure of
              business-specific measures of historical performance for
              portfolios (i.e. such as trademix by area or trademix by gross
              rent of lease expiry profile etc.);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Presence of financial
              highlights commentary (discussions on financial statistics
              presented like ROE, dividend cover, etc.);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Discussion on corporate
              liquidity or capital management (description of loan and borrowing
              profile);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Disclosure of
              REIT/business trust manager relationship;
            </li>

            <li>
              (Specific to REITs and Business Trusts) Earnings prospects by
              portfolios;
            </li>

            <li>
              (Specific to REITs and Business Trusts) Disclosure of group and
              strategic directions (clear articulation of vision, mission
              statement, goals and objectives);
            </li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Detailed Marking Criteria
          </p>

          <p>
            Annual reports shortlisted from the preliminary screening process
            are examined in detail for the quality of information disclosed
            according to the assessment criteria in the following areas:
          </p>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Performance Review (PR)
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>
              Commentaries on historical analysis and ratios (5-year, 10-year
              trends etc.);
            </li>

            <li>
              Analysis and commentaries on current year financials - quarterly,
              half-yearly, segment analysis;
            </li>

            <li>
              Commentaries on capital management (gearing, debt equity ratios,
              managing borrowing, refinancing etc.);
            </li>

            <li>
              Benchmarking against competitors and industry performance (e.g.
              total shareholder returns, share prices);
            </li>

            <li>
              Discussion on how the Group has performed in relation to its
              vision and mission;
            </li>

            <li>Group structure and business segment;</li>

            <li>
              (Specific to REITs and Business Trusts) Description of
              portfolio/asset characteristics (e.g. valuation as at latest date,
              committed occupancy, tenancy mix, top tenants, lease expiry
              profile);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Detailed description on
              yield accretive acquisitions and asset disposals (including
              disclosure of acquisition/disposal fee payment), asset enhancement
              initiatives, lease renewals;
            </li>

            <li>
              (Specific to REITs and Business Trusts) Current year ratios (DPU,
              distribution yields, gearing, interest cover and productivity) and
              commentaries;
            </li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Business Plan and Prospects (BPP)
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>
              Business plan and initiatives and planned capital investment
              (forward looking);
            </li>

            <li>
              Outlook and future prospects, including prospects by territory and
              business division;
            </li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Risk Assessment and Management (RAM)
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>
              Discussion on risk appetite, risk-aware culture and policies;
            </li>

            <li>
              Identification and assessment of key risks including
              climate-related risks;
            </li>

            <li>Risk management responses/strategies;</li>

            <li>Crisis management and business continuity planning;</li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Corporate Governance Issues (CG)
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>
              Discussion on the Group&apos;s policy and practices on diversity
              within the Board;
            </li>

            <li>Board assessment of director&apos;s independence;</li>

            <li>Board succession planning for independent directors;</li>

            <li>Succession planning for key management;</li>

            <li>Management background information (including shareholdings)</li>

            <li>
              Director remuneration (including breakdown, bands, dollar
              amounts);
            </li>

            <li>
              Key executive remuneration (including breakdown, bands, dollar
              amounts);
            </li>

            <li>Director orientation and training policy and processes;</li>

            <li>
              Key executive and director performance, evaluation and
              remuneration policy and processes;
            </li>

            <li>
              Fraud and whistle-blowing policy and processes (including
              procedures and any reported instances);
            </li>

            <li>
              Board opinion of internal controls (including Board&apos;s basis
              for the opinion);
            </li>

            <li>
              Commentary on the Group&apos;s key audit risk areas (e.g. audit
              committee/corporate governance report or a separate report);
            </li>

            <li>
              (Specific to REITs and Business Trusts) Relationship between
              sponsor, manager/trustee-manager and asset/property manager
              (roles/checks and balances residing with each entity, resolution
              of conflicts of interest particularly if there are common
              directors);
            </li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Environmental and Social Accountability (ESA)
          </p>

          <ul className="list-outside list-disc pb-6 pl-8">
            <li>
              Social and environmental sustainability practices, policy and
              plan;
            </li>

            <li>
              Quantitative and qualitative disclosures on environmental
              sustainability performance (including waste management, pollution
              control, energy conservation);
            </li>

            <li>
              Quantitative and qualitative disclosures on employees (including
              evidence on fair practices, training, non-discriminatory policy,
              health and safety policies);
            </li>

            <li>
              Quantitative and qualitative disclosures on wider community
              (including suppliers, customers, society);
            </li>

            <li>
              Discussion of the Group’s sustainability targets and performance
              and its progress in meeting those targets and performance;
            </li>

            <li>
              Discussion of sustainability reporting with reference to a GRI
              framework or reference to a separate sustainability report with
              the information (Additional points awarded for independent
              assurance on the report).
            </li>
          </ul>

          <p className="mb-2 text-xl font-medium md:text-2xl lg:text-4xl">
            Informativeness, Clarity and Presentation (ICP)
          </p>

          <ul className="list-outside list-disc pl-8">
            <li>
              Comprehensive and informative (i.e., no omission of crucial and
              useful information, investor/shareholder relations activities);
            </li>

            <li>
              Clear and well-organised (i.e., good use of diagrams/charts, easy
              retrieval of specific information);
            </li>

            <li>
              Innovative design and presentation (i.e., attractive, refreshing &
              novel presentation).
            </li>
          </ul>
        </div>
      </AccordionV2>
    </div>
  );
};

export default AnnualReportSection;
